import React, { useState, useEffect } from "react";
import { Routes, Route, Link as RouterLink, useNavigate, Navigate } from "react-router-dom";
import { Box, IconButton, Avatar, useMediaQuery, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from '@mui/icons-material/Menu';
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";

import AuthService from "./services/auth.service";
import RequestService from "./services/request.service";
import logo from "./assets/logo_scan.png";
import Sidebar from "./components/Sidebar";
import { usePreferences } from "./contexts/PreferencesContext";
import { getThemeColors } from "./config/theme";

import Login from "./components/Login";
import SignupMoved from "./components/SignupMoved";
import Profile from "./components/Profile";
import BoardUser from "./components/BoardUser";
import ForgotPassword from "./components/ForgotPassword";
import EmployeeList from "./components/EmployeeList";
import EmployeeProfilePage from "./components/employee/EmployeeProfilePage";
import WorkplaceManagement from "./components/WorkplaceManagement";
import VerifyEmail from "./components/VerifyEmail";
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import AdminUserManagement from "./components/AdminUserManagement";
import AutomationManagement from "./components/AutomationManagement";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Export from "./components/Export";
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import RequestManagement from "./components/RequestManagement";
import TestManagement from "./components/TestManagement";
import TimelineIcon from '@mui/icons-material/Timeline';
import ActivityManagement from "./components/ActivityManagement";
import CampaignIcon from '@mui/icons-material/Campaign';
import PsychologyIcon from '@mui/icons-material/Psychology';
import InAppMessageManagement from "./components/InAppMessageManagement";
import Asistent from "./components/asistent/AsistentPage";
import AiAgent from "./components/aiAgent/AiAgentPage";
import OnboardingFlow from "./components/onboarding/OnboardingFlow";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import ReportManagement from "./components/ReportManagement";
import AppAnalytics from "./components/AppAnalytics";
import AppVersions from "./components/AppVersions";
import SystemUpdateOutlinedIcon from "@mui/icons-material/SystemUpdateOutlined";
import Campaigns from "./components/Campaigns";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";

const App = () => {
  // Synchrónna inicializácia z localStorage — route guard pozná usera už pri
  // prvom renderi, takže F5 a priame linky nekončia presmerované na /login
  const [currentUser, setCurrentUser] = useState(() => AuthService.getCurrentUser() || undefined);
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [requestCount, setRequestCount] = useState(0);
  const navigate = useNavigate();
  const { t, isDarkMode } = usePreferences();
  const colors = getThemeColors(isDarkMode);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const refreshRequestCount = () => {
    if (currentUser) {
      RequestService.getPendingRequests()
        .then(response => {
          setRequestCount(response.data?.length || 0);
        })
        .catch(error => {
          console.error("Error fetching request count:", error);
          setRequestCount(0);
        });
    }
  };

  useEffect(() => {
    refreshRequestCount();
  }, [currentUser]);

  const handleSidebarOpen = () => {
    setSidebarOpen(true);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  const logOut = () => {
    AuthService.logout();
    setCurrentUser(undefined);
    handleSidebarClose();
    navigate("/login");
  };

  // Poradie podľa dennej frekvencie použitia; Testy sú dev artefakt — route
  // ostáva, ale z menu sú vyradené
  const menuItems = [
    { path: '/dashboard', text: t('sidebar.dashboard'), icon: <DashboardIcon />, section: 'main' },
    { path: '/requests', text: t('sidebar.requests'), icon: <PlaylistAddCheckIcon />, section: 'main' },
    { path: '/employees', text: t('sidebar.employees'), icon: <PeopleIcon />, section: 'main' },
    { path: '/workplaces', text: t('sidebar.workplaces'), icon: <BusinessIcon />, section: 'main' },
    { path: '/automations', text: t('sidebar.automations'), icon: <AutoModeIcon />, section: 'main' },
    { path: '/export', text: t('sidebar.export'), icon: <FileDownloadIcon />, section: 'main' },
    { path: '/asistent', text: t('sidebar.asistent'), icon: <PsychologyIcon />, section: 'main' },
    { path: '/activity', text: t('sidebar.activity'), icon: <TimelineIcon />, section: 'main' },
    { path: '/in-app-messages', text: t('sidebar.inAppMessages'), icon: <CampaignIcon />, section: 'broadcast' },
  ];

  if (currentUser && currentUser.role === 'admin') {
    menuItems.push({ path: '/admin/ai-agent', text: 'AI asistent', icon: <RecordVoiceOverIcon />, section: 'admin' });
    menuItems.push({ path: '/admin/reports', text: t('sidebar.support'), icon: <BugReportOutlinedIcon />, section: 'admin' });
    menuItems.push({ path: '/admin/analytics', text: 'Používanie', icon: <InsightsOutlinedIcon />, section: 'admin' });
    menuItems.push({ path: '/admin/app-versions', text: 'Verzie aplikácie', icon: <SystemUpdateOutlinedIcon />, section: 'admin' });
    menuItems.push({ path: '/admin/campaigns', text: 'Kampane', icon: <CampaignOutlinedIcon />, section: 'admin' });
    menuItems.push({ path: '/admin/users', text: t('sidebar.userManagement'), icon: <AdminPanelSettingsIcon />, section: 'admin' });
  }

  const isDesktopShell = currentUser && !isMobile;

  return (
    <Box
      sx={{
        display: "flex",
        height: isDesktopShell ? "100vh" : "auto",
        minHeight: isDesktopShell ? undefined : "100vh",
        position: "relative",
        overflow: isDesktopShell ? "hidden" : "visible",
        bgcolor: isDesktopShell ? colors.shellBackground : "background.default",
      }}
    >
      {/* Decorative background — desktop only */}
      {currentUser && !isMobile && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 0,
            pointerEvents: "none",
            bgcolor: colors.shellBackground,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "-10%",
              left: "-10%",
              width: 600,
              height: 600,
              borderRadius: "50%",
              bgcolor: isDarkMode ? "#1e3a8a" : "#DBEAFE",
              filter: "blur(150px)",
              opacity: isDarkMode ? 0.25 : 0.6,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: "-10%",
              right: "-10%",
              width: 600,
              height: 600,
              borderRadius: "50%",
              bgcolor: isDarkMode ? "#7c2d12" : "#FFEDD5",
              filter: "blur(150px)",
              opacity: isDarkMode ? 0.2 : 0.6,
            }}
          />
        </Box>
      )}

      {/* Mobile Header */}
      {currentUser && isMobile && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            backgroundColor: 'background.paper',
            borderBottom: `1px solid ${theme.palette.divider}`,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box
            component={RouterLink}
            to="/dashboard"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <Avatar src={logo} sx={{ mr: 1.5, width: 32, height: 32 }} />
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 'bold',
                color: 'text.primary',
                fontSize: '1rem',
              }}
            >
              {currentUser.company}
            </Typography>
          </Box>
          <IconButton
            size="large"
            aria-label="open sidebar"
            onClick={handleSidebarOpen}
            color="inherit"
          >
            <MenuIcon />
          </IconButton>
        </Box>
      )}

      {/* Sidebar */}
      {currentUser && (
        <>
          {/* Desktop Sidebar - Fixed */}
          {!isMobile && (
            <Box
              sx={{
                width: 352,
                flexShrink: 0,
                height: "100vh",
                p: 2,
                zIndex: 20,
                overflow: "hidden",
              }}
            >
              <Sidebar
                isOpen={true}
                onClose={handleSidebarClose}
                currentUser={currentUser}
                menuItems={menuItems}
                logOut={logOut}
                isMobile={false}
                requestCount={requestCount}
              />
            </Box>
          )}

          {/* Mobile Sidebar - Drawer */}
          {isMobile && (
            <Sidebar
              isOpen={isSidebarOpen}
              onClose={handleSidebarClose}
              currentUser={currentUser}
              menuItems={menuItems}
              logOut={logOut}
              isMobile={true}
              requestCount={requestCount}
            />
          )}
        </>
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          flex: isDesktopShell ? "1 1 0" : undefined,
          minWidth: 0,
          minHeight: isDesktopShell ? 0 : "100vh",
          height: isDesktopShell ? "100vh" : "auto",
          overflowY: isDesktopShell ? "auto" : "visible",
          bgcolor: currentUser ? "background.default" : "background.paper",
          pt: currentUser && isMobile ? "64px" : 0,
          position: "relative",
          zIndex: 10,
        }}
      >
        <Routes>
          <Route path="/" element={currentUser ? <Navigate to="/dashboard" /> : <Navigate to="/login" />} />
          <Route path="/login" element={currentUser ? <Navigate to="/dashboard" /> : <Login />} />
          {/* Registrácia firiem je na my.effzone.com (5. 11. 2026); prihlásenie ostáva tu. */}
          <Route path="/register" element={currentUser ? <Navigate to="/dashboard" /> : <SignupMoved />} />
          <Route path="/forgot-password" element={currentUser ? <Navigate to="/dashboard" /> : <ForgotPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/profile" element={currentUser ? <Profile /> : <Navigate to="/login" />} />
          <Route path="/dashboard" element={currentUser ? <BoardUser /> : <Navigate to="/login" />} />
          <Route path="/employees" element={currentUser ? <EmployeeList /> : <Navigate to="/login" />} />
          <Route path="/employees/:id" element={currentUser ? <EmployeeProfilePage /> : <Navigate to="/login" />} />
          <Route path="/workplaces" element={currentUser ? <WorkplaceManagement /> : <Navigate to="/login" />} />
          <Route path="/automations" element={currentUser ? <AutomationManagement /> : <Navigate to="/login" />} />
          <Route path="/tests" element={currentUser ? <TestManagement /> : <Navigate to="/login" />} />
          <Route path="/export" element={currentUser ? <Export /> : <Navigate to="/login" />} />
          <Route path="/requests" element={currentUser ? <RequestManagement onRequestAction={refreshRequestCount} /> : <Navigate to="/login" />} />
          <Route path="/activity" element={currentUser ? <ActivityManagement /> : <Navigate to="/login" />} />
          <Route path="/in-app-messages" element={currentUser ? <InAppMessageManagement /> : <Navigate to="/login" />} />
          <Route path="/asistent" element={currentUser ? <Asistent /> : <Navigate to="/login" />} />
          {currentUser && currentUser.role === 'admin' && (
            <Route path="/admin/ai-agent" element={<AiAgent />} />
          )}
          {currentUser && currentUser.role === 'admin' && (
            <Route path="/admin/reports" element={<ReportManagement />} />
          )}
          {currentUser && currentUser.role === 'admin' && (
            <Route path="/admin/analytics" element={<AppAnalytics />} />
          )}
          {currentUser && currentUser.role === 'admin' && (
            <Route path="/admin/app-versions" element={<AppVersions />} />
          )}
          {currentUser && currentUser.role === 'admin' && (
            <Route path="/admin/campaigns" element={<Campaigns />} />
          )}
          {currentUser && currentUser.role === 'admin' && (
            <Route path="/admin/users" element={<AdminUserManagement />} />
          )}
        </Routes>
      </Box>

      {/* Onboarding nových firiem: welcome → wizard → tour */}
      {currentUser && <OnboardingFlow currentUser={currentUser} />}
    </Box>
  );
};

export default App;
